import { featureFlags } from '@/lib/featureFlags'

import { ItemGroupMode } from '@VelocitySquared/sunny16-commerce-dataobjects/enums'
import type {
  BundleStorefrontDto,
  ItemGroupStorefrontDto
} from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'
import { CartItemUpsertStorefrontDto } from '@VelocitySquared/sunny16-commerce-dataobjects/storefront'

export function bundleHasSheetSelectionGroup(
  bundle: BundleStorefrontDto | null | undefined
): boolean {
  if (!featureFlags.packageBuilder) return false
  return bundle?.itemGroups?.some((ig) => ig.mode === ItemGroupMode.SheetSelection) ?? false
}

export function getSheetSelectionGroup(
  bundle: BundleStorefrontDto | null | undefined
): ItemGroupStorefrontDto | null {
  return bundle?.itemGroups?.find((ig) => ig.mode === ItemGroupMode.SheetSelection) ?? null
}

// Builds default selections for any item group that is not included in the bundle by default (isIncluded === false) yet
// still requires a minimum number of items (minRequiredItems > 0). The Package Builder submit path only captures the
// customer's SheetSelection picks, so without this the upsert payload would omit required items from these other groups
// and the configuration would be invalid. We auto-pick the lowest-zOrder items up to the required count; the customer
// can change them later. The SheetSelection group itself is excluded — the customer chooses those explicitly in the
// modal, so feeding it through here would duplicate their picks.
export function getAutoSelectedRequiredItems(
  bundle: BundleStorefrontDto | null | undefined,
  excludeItemGroupId?: string | null
): CartItemUpsertStorefrontDto[] {
  const result: CartItemUpsertStorefrontDto[] = []

  bundle?.itemGroups?.forEach((ig) => {
    if (ig.itemGroupId === excludeItemGroupId) return
    if (ig.isIncluded || ig.minRequiredItems <= 0) return

    // Copy before sorting so we don't mutate the source DTO's item order, then take the first minRequiredItems by zOrder.
    const sortedItems = [...(ig.itemGroupItems ?? [])]
      .sort((a, b) => (a.zOrder || 0) - (b.zOrder || 0))
      .slice(0, ig.minRequiredItems)

    sortedItems.forEach((igi) => {
      const cartItem = new CartItemUpsertStorefrontDto()
      cartItem.itemId = igi.itemId
      cartItem.itemGroupItemId = igi.itemGroupItemId
      result.push(cartItem)
    })
  })

  return result
}

// Builds a valid *default* selection for the bundle's SheetSelection group(s): `maxAllowedItems`
// copies of the lowest-zOrder eligible sheet, unrolled to one `quantity: 1` cart item each.
//
// Sunny16 rejects an add whose SheetSelection group doesn't total exactly `maxAllowedItems`, and the
// add-time `CartBundleConfigurator` otherwise leaves the group empty (its `minRequiredItems` is 0).
// So a freshly-added Package Builder bundle needs a valid default to enter the cart at all; the
// customer then refines it on the Package Builder step, which overwrites the bundle via
// `upsertCartBundle`. Per business decision (2026-06-23), the default piles the whole allowance onto
// the single lowest-zOrder sheet rather than distributing.
//
// Flag-gated so the package-builder kill switch keeps its degraded-off behavior (SheetSelection
// bundles fall through to the unseeded add path, which the backend rejects — as documented on the
// `packageBuilder` feature flag).
export function getDefaultSheetSelectionItems(
  bundle: BundleStorefrontDto | null | undefined
): CartItemUpsertStorefrontDto[] {
  if (!featureFlags.packageBuilder) return []

  const result: CartItemUpsertStorefrontDto[] = []

  bundle?.itemGroups?.forEach((ig) => {
    if (ig.mode !== ItemGroupMode.SheetSelection) return

    const target = ig.maxAllowedItems ?? 0
    if (target <= 0) return

    // Copy before sorting so we don't mutate the source DTO's item order, then take the
    // lowest-zOrder eligible sheet.
    const lowestZOrderItem = [...(ig.itemGroupItems ?? [])].sort(
      (a, b) => (a.zOrder || 0) - (b.zOrder || 0)
    )[0]
    if (!lowestZOrderItem) return

    for (let i = 0; i < target; i++) {
      const cartItem = new CartItemUpsertStorefrontDto()
      cartItem.itemId = lowestZOrderItem.itemId
      cartItem.itemGroupItemId = lowestZOrderItem.itemGroupItemId
      cartItem.quantity = 1
      cartItem.cartItemCustomizationDetails = { promptResponses: [] }
      result.push(cartItem)
    }
  })

  return result
}
